/**
 * @file Frontend utility — a3ManufacturingSpecificityField.
 */

import type { FormField } from "../interfaces/formInterfaces";

/**
 * True for the "Manufacturing Specificity" field in Manufacturing Process Details.
 * CMS may use different subsection `code` / field `type`; we key off label + name, not only
 * MANUFACTURER_SPECIFICATION.
 */
export function isA3ManufacturingSpecificityFromBackgroundField(
  f: FormField,
  sectionCode: string,
  sectionName?: string | null
): boolean {
  const code = String(sectionCode || "").toLowerCase();
  const title = String(sectionName || "").toLowerCase();
  const inProcessDetailsSection =
    code === "a3_process_details" ||
    (title.includes("manufacturing process") && !title.includes("plant"));

  const nameEarly = String(f.name || "").toLowerCase();
  if (nameEarly === "manufacturingtechnology_a3") return true;

  if (!inProcessDetailsSection) return false;

  // Actual manufacturing-process step picker (different field).
  if (f.type === "MANUFACTURING_PROCESS") return false;

  const label = String(f.label || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
  const name = nameEarly;

  const labelMatch =
    label.includes("manufacturing specificity") ||
    (label.includes("manufacturing") && label.includes("specificity"));
  const nameMatch =
    name.includes("manufacturingspecificity") ||
    name.includes("manufacturing_specificity");

  if (!labelMatch && !nameMatch) return false;

  // "Manufacturing process" (steps) ≠ "Manufacturing specificity" (background technology)
  if (
    label.includes("manufacturing process") &&
    !label.includes("specificity")
  ) {
    return false;
  }

  return true;
}

/** Type definition — A3Background Technology Slice. */
export type A3BackgroundTechnologySlice = {
  technology?: { id?: number; name?: string | null; description?: string | null } | null;
  technologyId?: number | string | null;
  technologyValue?: string | null;
};

/** Label + id string for form state / display. */
export function readBackgroundTechnologyForA3(
  bg: A3BackgroundTechnologySlice | null | undefined
): { id: string; display: string } {
  if (!bg) return { id: "", display: "" };

  const raw = bg.technology as
    | { id?: number; name?: string | null }
    | { id?: number; name?: string | null }[]
    | null
    | undefined;
  const techObj = Array.isArray(raw) ? raw[0] : raw;

  const nestedId = techObj?.id ?? bg.technology?.id;
  const flatId = bg.technologyId;
  const id =
    nestedId != null && String(nestedId).trim() !== ""
      ? String(nestedId)
      : flatId != null && String(flatId).trim() !== ""
        ? String(flatId)
        : "";
  const display =
    String(techObj?.name ?? bg.technology?.name ?? "").trim() ||
    String(bg.technologyValue ?? "").trim() ||
    id;
  return { id, display };
}
